/**
 * D420 (owner decision D408 #9): why a market's status changed — the closed vocabulary the server accepts (mirrors
 * `@vello/config` `MARKET_STATUS_REASON_CODES`; the Admin's `MARKET_STATUS_REASONS` is held equal to it by a test).
 */
export const MARKET_STATUS_REASON_CODES = [
    'market_launch_approved',
    'market_pilot_extended',
    'market_paused_by_operations',
    'market_regulatory_hold',
    'market_retired_end_of_service',
    'market_other',
];
export class MarketsApi {
    client;
    constructor(client) {
        this.client = client;
    }
    /** Register an operating market (city) in the catalog. The server builds the typed `cc:slug` id. */
    registerMarket(input) {
        return this.client.request({ method: 'POST', path: '/v1/config/markets', body: input, auth: true });
    }
    /** The market catalog — all registered markets, or one country's when `country` is given. */
    listMarkets(country) {
        const qs = country !== undefined ? `?country=${encodeURIComponent(country)}` : '';
        return this.client.request({ method: 'GET', path: `/v1/config/markets${qs}`, auth: true });
    }
    /**
     * STATE-SCOPE S1: the administrative-region (state) catalog - all registered regions, or one country's when `country` is given. Any authenticated operator
     * session (the scope selector's third level reads it); authority to act in a state stays the scoped grant.
     */
    regions(country) {
        const qs = country !== undefined ? `?country=${encodeURIComponent(country)}` : '';
        return this.client.request({ method: 'GET', path: `/v1/config/regions${qs}`, auth: true });
    }
    /**
     * ADR-0124 (the regions console): rename a state and/or retire / reactivate it (`market:manage` at the platform, audited). A retired state keeps the markets and
     * grants it already has and takes no new market, grant, audience or banner. `400 REGION_NAME_INVALID | REGION_STATUS_INVALID | REGION_CHANGE_EMPTY`,
     * `404 REGION_NOT_FOUND`, `409 REGION_UNCHANGED`.
     */
    updateRegion(code, change) {
        return this.client.request({ method: 'PUT', path: `/v1/config/regions/${encodeURIComponent(code)}`, body: change, auth: true });
    }
    /**
     * Change a market's lifecycle status (e.g. `DRAFT` → `ACTIVE` at launch). D420: the reason is required and recorded
     * with an audit row (`400 MARKET_STATUS_REASON_REQUIRED` without it; `409 MARKET_STATUS_UNCHANGED` for the same status).
     */
    setMarketStatus(id, status, reason) {
        const body = { status, reasonCode: reason.reasonCode, ...(reason.note !== undefined && reason.note.trim() !== '' ? { note: reason.note.trim() } : {}) };
        return this.client.request({ method: 'POST', path: `/v1/config/markets/${encodeURIComponent(id)}/status`, body, auth: true });
    }
    /** D420: the market's recorded status changes, newest first (`market:manage`). */
    statusHistory(id) {
        return this.client.request({ method: 'GET', path: `/v1/config/markets/${encodeURIComponent(id)}/status-history`, auth: true });
    }
    /**
     * D449: propose making a market ACTIVE (`market:manage`; 202). `setMarketStatus(…, 'ACTIVE')` is refused with
     * `409 MARKET_ACTIVE_REQUIRES_FOUR_EYES`; another operator with `market:approve` decides the proposal.
     */
    proposeActivation(id, reason) {
        const body = { reasonCode: reason.reasonCode, ...(reason.note !== undefined && reason.note.trim() !== '' ? { note: reason.note.trim() } : {}) };
        return this.client.request({ method: 'POST', path: `/v1/config/markets/${encodeURIComponent(id)}/activation`, body, auth: true });
    }
    /** D449: the activation proposals (default PENDING), optionally for one market. */
    listActivations(filter = {}) {
        const q = new URLSearchParams();
        if (filter.state !== undefined)
            q.set('state', filter.state);
        if (filter.marketId !== undefined)
            q.set('marketId', filter.marketId);
        const qs = q.toString() !== '' ? `?${q.toString()}` : '';
        return this.client.request({ method: 'GET', path: `/v1/config/market-activations${qs}`, auth: true });
    }
    /** D449: approve / reject (a different operator; a rejection needs a note) or withdraw (the proposer). */
    decideActivation(requestId, action, note) {
        const body = note !== undefined && note.trim() !== '' ? { note: note.trim() } : {};
        return this.client.request({ method: 'POST', path: `/v1/config/market-activations/${encodeURIComponent(requestId)}/${action}`, body, auth: true });
    }
}
//# sourceMappingURL=markets.js.map